/**
 * Router — Project Harmony
 *
 * 5 primary destinations map to 5 hub pages:
 *   /            → HomePage
 *   /campaigns   → CampaignsHubPage      (+ /campaigns/new, /campaigns/:id/room, …)
 *   /vault       → VaultHubPage          (+ /library, /media as internal)
 *   /publishing  → PublishingHubPage     (+ /publishing/calendar → alias of /mission-control)
 *   /workspace   → WorkspaceHubPage      (+ /workspace/autopilot as ambient view)
 *
 * Legacy URLs remain as **intelligent aliases** (Executive Directive · Option A):
 * bookmarks and docs keep working; users land at the new experience.
 */
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import { AuthGuard } from "@/app/AuthGuard";
import { RootLayout } from "@/app/layout";
import { ScrollToTop } from "@/app/ScrollToTop";
import { useAuthStore } from "@/entities/auth/store";
import { WorkspaceCreatePage } from "@/features/workspace-create";
import { AIStudioPage } from "@/pages/ai-studio/AIStudioPage";
import { AIStudioPackDetailPage } from "@/pages/ai-studio/AIStudioPackDetailPage";
import { BrainMissionPage } from "@/features/brain-mission";
import { ContentOpsPage } from "@/pages/ops/ContentOpsPage";
import { ReviewSessionPage } from "@/features/review-session";
import { DashboardPage } from "@/pages/dashboard/DashboardPage";
import { GenerationHistoryPage } from "@/pages/generation-history/GenerationHistoryPage";
import { HealthPage } from "@/pages/health/HealthPage";
import IntelligencePage from "@/pages/intelligence/IntelligencePage";
import MissionControlPage from "@/pages/mission-control/MissionControlPage";
import LibraryPage from "@/pages/library/LibraryPage";
import MediaPage from "@/pages/media/MediaPage";
import ChannelsPage from "@/pages/channels/ChannelsPage";
import PublishingHistoryPage from "@/pages/publishing-history/PublishingHistoryPage";
import KnowledgePage from "@/pages/knowledge/KnowledgePage";
import AssetDetailPage from "@/pages/asset-detail/AssetDetailPage";
import AuthPage, { AuthCallbackPage } from "@/pages/auth/AuthPage";
import BillingPage from "@/pages/billing/BillingPage";
import DownloadsPage from "@/pages/downloads/DownloadsPage";
import FocusFlowDemoPage from "@/pages/focus-flow-demo/FocusFlowDemoPage";
import PublishingPage from "@/pages/publishing/PublishingPage";
import LaunchReadinessPage from "@/pages/launch-readiness/LaunchReadinessPage";
import LandingPage from "@/pages/landing/LandingPage";
import AcademyPage from "@/pages/academy/AcademyPage";
import OnboardingPage from "@/pages/onboarding/OnboardingPage";
import DemoPage from "@/pages/demo-public/DemoPage";
import WorkspaceDemoEntryPage from "@/pages/demo-public/WorkspaceDemoEntryPage";
import BrainBuilderDemoPage from "@/pages/demo-public/BrainBuilderDemoPage";
import PricingPage from "@/pages/pricing/PricingPage";
import ScannerPage from "@/pages/scanner/ScannerPage";
import ProductionAccountProjectionPage from "@/pages/production-account-projection/ProductionAccountProjectionPage";
import WorkspaceSettingsPage from "@/pages/workspace-settings/WorkspaceSettingsPage";
import WorkspaceBusinessDiscoveryPage from "@/pages/workspace-business-discovery/WorkspaceBusinessDiscoveryPage";
import CampaignCommanderPage from "@/pages/campaign-commander/CampaignCommanderPage";
import MarketingStrategyPage from "@/pages/marketing-strategy/MarketingStrategyPage";
import CampaignRoomPage from "@/pages/campaign-room/CampaignRoomPage";

// Project Harmony pages
import HomePage from "@/pages/home/HomePage";
import CampaignsHubPage from "@/pages/campaigns-hub/CampaignsHubPage";
import VaultHubPage from "@/pages/vault-hub/VaultHubPage";
import PublishingHubPage from "@/pages/publishing-hub/PublishingHubPage";
import WorkspaceHubPage from "@/pages/workspace-hub/WorkspaceHubPage";
import WorkspaceAutopilotPage from "@/pages/workspace-autopilot/WorkspaceAutopilotPage";
import GrowthNetworkPage from "@/pages/growth-network/GrowthNetworkPage";
import GrowthReferralJoinPage from "@/pages/growth-network/GrowthReferralJoinPage";

// Project Genesis — the signature adoption experience.
import GenesisPage from "@/pages/genesis/GenesisPage";

export function AppRouter(): JSX.Element {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<RootRedirect />} />
        <Route path="/welcome" element={<LandingPage />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/demo" element={<DemoPage />} />
        <Route path="/demo/workspace" element={<WorkspaceDemoEntryPage />} />
        <Route path="/demo/brain-builder" element={<BrainBuilderDemoPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/scanner" element={<ScannerPage />} />
        <Route path="/login" element={<AuthPage />} />
        <Route path="/join/:referralCode" element={<GrowthReferralJoinPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route element={<AuthGuard />}>
          {/* Project Genesis is full-bleed — auth required but no RootLayout chrome. */}
          <Route path="/genesis" element={<GenesisPage />} />
          <Route element={<RootLayout />}>
            {/* ─── Project Harmony · Primary Destinations (5) ─── */}
            <Route path="/home" element={<HomePage />} />

            <Route path="/campaigns" element={<CampaignsHubPage />} />
            <Route path="/campaigns/new" element={<CampaignCommanderPage />} />
            <Route path="/campaigns/:blueprintId/strategy" element={<MarketingStrategyPage />} />
            <Route path="/campaigns/:blueprintId/room" element={<CampaignRoomPage />} />

            <Route path="/vault" element={<VaultHubPage />} />

            <Route path="/publishing" element={<PublishingHubPage />} />
            <Route path="/publishing/calendar" element={<MissionControlPage />} />
            <Route path="/publishing/scheduled" element={<PublishingPage />} />
            <Route path="/publishing/queued" element={<PublishingPage />} />
            <Route path="/publishing/published" element={<PublishingPage />} />
            <Route path="/publishing/platforms" element={<PublishingPage />} />
            <Route path="/publishing/history" element={<GenerationHistoryPage />} />
            <Route path="/channels" element={<ChannelsPage />} />
            <Route path="/publishing/channels" element={<ChannelsPage />} />
            <Route path="/publishing/history-log" element={<PublishingHistoryPage />} />

            <Route path="/workspace" element={<WorkspaceHubPage />} />
            <Route path="/workspace/autopilot" element={<WorkspaceAutopilotPage />} />
            <Route path="/workspace/business-discovery" element={<WorkspaceBusinessDiscoveryPage />} />
            <Route path="/workspace/growth-network" element={<GrowthNetworkPage />} />
            <Route path="/workspace/diagnostics" element={<PlatformAdminRoute><HealthPage /></PlatformAdminRoute>} />
            {/* Patch 34.1.20 — founder-only downloads (Bearer JWT via app session). */}
            <Route path="/workspace/downloads" element={<DownloadsPage />} />
            <Route
              path="/workspace/production-account-projection"
              element={<ProductionAccountProjectionPage />}
            />

            {/* Content Review — canonical customer-facing route (Amendment 12). */}
            <Route path="/campaigns/review" element={<ContentOpsPage />} />
            <Route path="/review" element={<Navigate to="/campaigns/review" replace />} />
            <Route path="/ops" element={<Navigate to="/campaigns/review" replace />} />
            <Route path="/content-review" element={<Navigate to="/campaigns/review" replace />} />

            {/* Milestone 37.3 · Review Session UX — Focus Flow-powered
                guided review of a single campaign pack. Separate from
                the list-based ContentOpsPage above — both remain. */}
            <Route
              path="/campaigns/:packId/review-session"
              element={<ReviewSessionPage />}
            />

            {/* ─── Legacy aliases (Executive Option A) ─── */}
            {/* Old top-level → new destinations. Every URL still works. */}
            <Route path="/command-center" element={<Navigate to="/home" replace />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/intelligence" element={<IntelligencePage />} />
            <Route path="/mission-control" element={<Navigate to="/publishing/calendar" replace />} />
            <Route path="/library" element={<LibraryPage />} />
            <Route path="/media" element={<MediaPage />} />
            <Route path="/knowledge" element={<KnowledgePage />} />
            <Route path="/assets/:permanentAssetId" element={<AssetDetailPage />} />
            <Route path="/history" element={<GenerationHistoryPage />} />
            <Route path="/health" element={<PlatformAdminRoute><HealthPage /></PlatformAdminRoute>} />
            <Route path="/studio" element={<AIStudioPage />} />
            <Route path="/ai-studio" element={<AIStudioPage />} />
            <Route path="/ai-studio/packs/:packId" element={<AIStudioPackDetailPage />} />
            <Route path="/academy" element={<AcademyPage />} />
            <Route path="/launch-readiness" element={<PlatformAdminRoute><LaunchReadinessPage /></PlatformAdminRoute>} />
            <Route path="/billing" element={<BillingPage />} />
            <Route path="/billing/success" element={<BillingPage />} />
            <Route path="/billing/cancel" element={<BillingPage />} />
            <Route
              path="/brand-brain"
              element={<BrainMissionPage key="brand-brain-guided" overrideType="brand_brain" />}
            />
            {/* Patch 37.4.3 — "/brand-brain/advanced" is retained (per
                the exec directive: do not create a new parallel route)
                but now opens the Full Brain Editor with the same
                canonical question catalog + backend mappings as Focus
                Flow. Customer label is "Edit All Answers" / "Full
                Brain Editor" — the word "advanced" is never surfaced.
                A distinct ``key`` forces React to unmount the guided
                instance and mount a fresh Full Edit instance on
                route change, so per-mission state (phase, dirty
                fields) never bleeds across modes. */}
            <Route
              path="/brand-brain/advanced"
              element={<BrainMissionPage key="brand-brain-full-edit" overrideType="brand_brain" forceFullEdit />}
            />
            <Route
              path="/business-brain"
              element={<BrainMissionPage key="business-brain-guided" overrideType="business_brain" />}
            />
            <Route
              path="/business-brain/advanced"
              element={<BrainMissionPage key="business-brain-full-edit" overrideType="business_brain" forceFullEdit />}
            />
            <Route path="/settings" element={<WorkspaceSettingsPage />} />
            <Route path="/workspaces/new" element={<WorkspaceCreatePage />} />
            <Route path="/campaign-commander" element={<Navigate to="/campaigns/new" replace />} />

            {/* Milestone 37.1 · Focus Flow demo — gated by backend
                E2E-mode probe. In production the page immediately
                redirects to /home; the ``/api/v1/e2e/focus-flow/enabled``
                endpoint returns 404 outside E2E. Never linked in nav. */}
            <Route path="/e2e/focus-flow-demo" element={<FocusFlowDemoPage />} />

            <Route path="*" element={<RootRedirect />} />
          </Route>
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

/** Root gate: authenticated → Home; guest → Home; else → landing.
 *
 *  Patch 34.1.10 — Landing page is the default public entry. Auto-login
 *  no longer runs on ``/`` — visitors choose their demo experience
 *  explicitly from the landing page (Ready-to-Work Demo or Brain
 *  Builder Demo).
 */
function RootRedirect(): JSX.Element {
  const token = useAuthStore((s) => s.accessToken);
  const guestActive = useAuthStore((s) => s.guest.active);
  if (token) return <Navigate to="/home" replace />;
  if (guestActive) return <Navigate to="/home" replace />;
  return <Navigate to="/welcome" replace />;
}

function PlatformAdminRoute({ children }: { children: JSX.Element }): JSX.Element {
  const isPlatformAdmin = useAuthStore((s) => s.user?.is_dev_user === true);
  return isPlatformAdmin ? children : <Navigate to="/home" replace />;
}
